import * as _ from 'lodash';
import { Observable, from, of, forkJoin } from 'rxjs';
import { switchMap, tap, catchError, shareReplay } from 'rxjs/operators';
import { SelectionSummaryHelper } from '../content/selection-summary.helper';

export class PayloadBuilderHelper {
  static getOrchestrationRequest(comp: any): Observable<any> {
    return from(comp.getBearerToken()).pipe(
      tap(token => comp.currentOrchToken = token),
      switchMap(token => {
        const modules = comp.modulesAccess || {};

        const findModuleAccess = (searchKey: string) => {
          const actualKey = Object.keys(modules).find(k => k.toLowerCase() === searchKey.toLowerCase());
          return actualKey ? modules[actualKey]?.has_access : false;
        };

        const hasSnapshot = findModuleAccess('snapshot');
        const hasCrosstab = findModuleAccess('crosstab');
        const hasBrandAnalysis = findModuleAccess('brand_analysis') || findModuleAccess('brandanalysis');

        const activeCount = [hasSnapshot, hasCrosstab, hasBrandAnalysis].filter(v => v).length;
        comp.hasSnapshotActive = hasSnapshot;
        comp.hasCrosstabActive = hasCrosstab;
        comp.hasBrandAnalysisActive = hasBrandAnalysis;

        const currentWidgetDetails = {
          widget_name: comp.widget_Name || '',
          widget_id: comp.widget_Id || ''
        };

        // CASE 1: Single Module Access (Direct Specialized Call)
        if (activeCount === 1) {
          if (hasSnapshot) {
            return comp.apiService.snapshotAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, comp.currentQueryForStream, true, false, true, 0, currentWidgetDetails);
          } else if (hasCrosstab) {
            return comp.apiService.crosstabAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, comp.currentQueryForStream, true, false, true, 0, currentWidgetDetails);
          } else if (hasBrandAnalysis) {
            return comp.apiService.brandAnalysisAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, comp.currentQueryForStream, true, false, true, 0, currentWidgetDetails);
          }
        }

        // CASE 2: Multi-Module Access (Mcode Decider Strategy)
        if (activeCount > 1) {

          return new Observable(observer => {
            const decider$ = comp.apiService.mcodeChatAgentStream(
              comp.selected_client, comp.selected_study, comp.guidChatId, token, comp.currentQueryForStream,
              true, false, hasSnapshot, hasCrosstab, hasBrandAnalysis, true, 0
            ).pipe(shareReplay(1));

            // Helper to execute FULL lifecycle (Stream + Payload) for a module
            const executeTask = (mcode: number, query: string, primaryMcode: number): Observable<any> => {
              const msgId: string = comp.selectedMessageId || (comp.chatPreviewBlocks.length > 0 ? (comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId || 'current') : 'current');
              if (!comp.inFlightPreFetches[msgId]) comp.inFlightPreFetches[msgId] = new Set();
              comp.inFlightPreFetches[msgId].add(mcode);

              const currentWidgetDetails = {
                widget_name: comp.widget_Name || '',
                widget_id: comp.widget_Id || ''
              };

              const stream$ = mcode === 1 ? comp.apiService.snapshotAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, query, true, false, true, 0, currentWidgetDetails) :
                (mcode === 2 ? comp.apiService.brandAnalysisAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, query, true, false, true, 0, currentWidgetDetails) :
                  comp.apiService.crosstabAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, query, true, false, true, 0, currentWidgetDetails));

              return new Observable(obs => {
                let streamMetadata: any = null;
                const sSub = stream$.subscribe({
                  next: (event: any) => {
                    if (event && event.event === 'final_result') {
                      streamMetadata = event;
                    } else if (event && event.event === 'status') {
                      obs.next(event);
                    } else if (event && event.event !== 'done' && event.event !== 'meta') {
                      obs.next(event);
                    }
                  },
                  error: (err: any) => {
                    comp.inFlightPreFetches[msgId].delete(mcode);
                    obs.error(err);
                  },
                  complete: () => {
                    if (!streamMetadata) {
                      comp.inFlightPreFetches[msgId].delete(mcode);
                      obs.error(`Stream ${mcode} result missing`);
                      return;
                    }

                    const rawData = streamMetadata.data;
                    const resData = rawData.result || rawData.final_output || rawData;
                    const payloadParams = {
                      session_id: comp.validatedSessionId || msgId,
                      study: comp.selected_study,
                      client_name: comp.selected_client,
                      bearer_token: token, mcode: mcode, significance: "None", confidence_level: "None",
                      base: "ANSWER", weight: resData.weight || resData.Weight || comp.selectedWeight || "Unweighted"
                    };

                    let specialized: any = null;
                    if (mcode === 1) {
                      comp.widget_Id = resData.Widget_ID || resData.widget_id || comp.widget_Id;
                      comp.widget_Name = resData.Widget_Name || resData.widget_name || comp.widget_Name;
                      specialized = { ...payloadParams, snapshot_type: resData.snapshot_type || resData.Snapshot_Type, primary_brands: resData.Primary_Brands || resData.primary_brands, primary_metrics: resData.Primary_Metrics || resData.primary_metrics || resData.primary_metircs, market: resData.Markets || resData.market, timeperiod: resData.Time_Period || resData.timeperiod, Filter_Audience: resData.Filter_Audience || resData.filter_audience, Metric_Filters: resData.Metric_Filters || resData.metric_filters, Snapshot_Name: resData.Snapshot_Name || resData.snapshot_name, Section: resData.Section || resData.section, Widget_ID: comp.widget_Id, Widget_Name: comp.widget_Name, selected_doc_id: resData.selected_doc_id, snapshot_index: rawData.snapshot_index, widget_snapshot_index: rawData.widget_snapshot_index };
                    } else {
                      specialized = { ...payloadParams, row: resData.Row, column: resData.Column, row_nesting: resData.Row_Nesting, column_nesting: resData.Column_Nesting, filters: resData.Filters, brand_analysis_index: rawData.brand_analysis_index, crosstab_index: rawData.crosstab_index };
                    }

                    const payload$ = mcode === 1 ? comp.apiService.snapshotPayloadAPI(specialized) :
                      (mcode === 2 ? comp.apiService.brandAnalysisPayloadAPI(specialized) :
                        comp.apiService.crosstabPayloadAPI(specialized));

                    payload$.subscribe({
                      next: (payloadRes: any) => {
                        const dataStr = payloadRes?.response?.Data || payloadRes?.Data;
                        const isEmpty = dataStr === '[]' || dataStr === '"{}"' || dataStr === '{}' || (Array.isArray(dataStr) && dataStr.length === 0);

                        if (isEmpty || !dataStr) {
                          console.warn(`[ORCHESTRATION] Payload returned empty Data for mcode: ${mcode}. Disqualifying module.`);
                          comp.inFlightPreFetches[msgId].delete(mcode);
                          obs.error(`Empty data for mcode ${mcode}`);
                          return;
                        }

                        // Populate cache immediately so main flow can use it
                        if (!comp.multiViewDataCache[msgId]) comp.multiViewDataCache[msgId] = {};
                        comp.multiViewDataCache[msgId][mcode] = payloadRes;

                        // Emit immediately to unblock UI render!
                        obs.next({
                          event: 'final_result',
                          mcode: mcode,
                          isPreFetched: true,
                          data: rawData,
                          payloadParams: specialized,
                          payload: payloadRes,
                          insights: null
                        });
                        obs.complete();

                        // Fire and forget insights fetch for background caching (ONLY for secondary modules)
                        if (mcode !== primaryMcode) {
                          comp.fetchInsightsAsObservable(mcode, payloadRes, msgId, specialized).subscribe({
                            next: (insightsResult: any) => {
                              comp.inFlightPreFetches[msgId].delete(mcode);
                            },
                            error: (err: any) => {
                              console.warn(`[ORCHESTRATION] Insights pre-fetch failed for mcode: ${mcode} in background.`, err);
                              comp.inFlightPreFetches[msgId].delete(mcode);
                            }
                          });
                        } else {
                          // For primary module, we skip background fetch so callInsightsAPI can stream it to the UI!
                          comp.inFlightPreFetches[msgId].delete(mcode);
                        }
                      },
                      error: (err: any) => {
                        comp.inFlightPreFetches[msgId].delete(mcode);
                        obs.error(err);
                      }
                    });
                  }
                });
                return () => sSub.unsubscribe();
              });
            };

            const deciderSub = decider$.subscribe({
              next: (event: any) => {
                if (event && event.event === 'status') {
                  observer.next(event);
                  return;
                }
                if (event && event.event !== 'final_result' && event.event !== 'done' && event.event !== 'meta') {
                  observer.next(event);
                  return;
                }

                if (event && event.event === 'final_result' && event.data) {
                  const rawData = event.data;
                  const resData = rawData.result || rawData;
                  const rankingStr = resData.priority_order || resData.mcode_ranking || '';
                  let priorityMcode = resData.priority_mcode;
                  const optimizedQuery = resData.resolved_question || resData.question || comp.inputValue;

                  deciderSub.unsubscribe();

                  let mcodeRanking: number[] = [];
                  if (rankingStr && typeof rankingStr === 'string' && rankingStr.trim() !== '') {
                    const digits = rankingStr.match(/\d+/g)?.map(d => parseInt(d)) || [];
                    mcodeRanking = rankingStr.includes('=') && !rankingStr.includes('>') ? [...digits].reverse() : [...digits];
                  } else if (priorityMcode) {
                    const p = parseInt(priorityMcode.toString());
                    if (p === 1) mcodeRanking = [1, 2, 3];
                    else if (p === 2) mcodeRanking = [2, 1, 3];
                    else if (p === 3) mcodeRanking = [3, 2, 1];
                    else mcodeRanking = [p];
                  }

                  mcodeRanking = mcodeRanking.filter(m => (m === 1 && hasSnapshot) || (m === 2 && hasBrandAnalysis) || (m === 3 && hasCrosstab));
                  [1, 2, 3].forEach(m => {
                    if (((m === 1 && hasSnapshot) || (m === 2 && hasBrandAnalysis) || (m === 3 && hasCrosstab)) && !mcodeRanking.includes(m)) {
                      mcodeRanking.push(m);
                    }
                  });
                  mcodeRanking = Array.from(new Set(mcodeRanking));

                  if (mcodeRanking.length === 0) { observer.error('No valid mcodes'); return; }

                  // Parallel Pre-fetch all Tasks
                  const taskMap: { [key: number]: Observable<any> } = {};
                  if (hasSnapshot) taskMap[1] = executeTask(1, optimizedQuery, priorityMcode).pipe(shareReplay(1));
                  if (hasBrandAnalysis) taskMap[2] = executeTask(2, optimizedQuery, priorityMcode).pipe(shareReplay(1));
                  if (hasCrosstab) taskMap[3] = executeTask(3, optimizedQuery, priorityMcode).pipe(shareReplay(1));

                  // Start all tasks: forward status ONLY for priority module, others run silently in background
                  Object.entries(taskMap).forEach(([key, t$]) => {
                    const mcodeNum = parseInt(key);
                    t$.subscribe({
                      next: (event: any) => {
                        if (event && event.event === 'status' && mcodeNum === priorityMcode) {
                          observer.next(event);
                        }
                      },
                      error: (err: any) => console.warn(`[ORCHESTRATION] Task stream error for mcode ${key}:`, err)
                    });
                  });

                  // Parallel execution of all tasks, collecting all successes for multi-view
                  const allTasks$ = forkJoin(
                    Object.keys(taskMap).reduce((acc: any, key) => {
                      const m = parseInt(key);
                      acc[key] = taskMap[m].pipe(
                        catchError((err) => {
                          console.warn(`[ORCHESTRATION] Task ${m} failed:`, err);
                          if (m === priorityMcode) {
                            const idx = mcodeRanking.indexOf(m);
                            const nextMcode = mcodeRanking[idx + 1];
                            if (nextMcode) {
                              const moduleName = nextMcode === 1 ? 'Snapshot' : (nextMcode === 2 ? 'Brand Analysis' : 'Crosstab');
                              observer.next({ event: 'status', message: `Primary module failed. Switching to ${moduleName}...` });
                              priorityMcode = nextMcode;
                            }
                          }
                          return of(null);
                        })
                      );
                      return acc;
                    }, {})
                  );

                  allTasks$.subscribe({
                    next: (results: any) => {
                      const primaryMcode = mcodeRanking.find(m => results[m] && results[m].event === 'final_result');

                      if (!primaryMcode) {
                        observer.error('All analytical agents failed to return a result.');
                        return;
                      }

                      const primaryResult = results[primaryMcode];

                      // Gather all successful payloads into member_responses for multi-view switcher
                      const member_responses: any = {};
                      Object.keys(results).forEach(key => {
                        const res = results[key];
                        if (res && res.event === 'final_result') {
                          const m = parseInt(key);
                          const moduleKey = m === 1 ? 'snapshot' : (m === 2 ? 'brand_analysis' : 'crosstab');
                          member_responses[moduleKey] = res.data?.result || res.data?.final_output || res.data || {};
                        }
                      });

                      // Attach member_responses to the primary result's data object
                      if (primaryResult.data) {
                        primaryResult.data.member_responses = {
                          ...(primaryResult.data.member_responses || {}),
                          ...member_responses
                        };
                      }

                      observer.next(primaryResult);
                      observer.complete();
                    },
                    error: (err: any) => observer.error(err)
                  });
                }
              },
              error: (err: any) => {
                console.error('[ORCHESTRATION] Decider failed. Absolute fallback.');
                const fallbackRanking = [1, 2, 3].filter(m => (m === 1 && hasSnapshot) || (m === 2 && hasBrandAnalysis) || (m === 3 && hasCrosstab));
                if (fallbackRanking.length === 0) { observer.error('No access'); return; }

                const firstFallbackName = fallbackRanking[0] === 1 ? 'Snapshot' : (fallbackRanking[0] === 2 ? 'Brand Analysis' : 'Crosstab');
                observer.next({ event: 'status', message: `Routing failed. Falling back to ${firstFallbackName}...` });

                const fbMap: { [key: number]: Observable<any> } = {};
                fallbackRanking.forEach(m => fbMap[m] = executeTask(m, comp.inputValue, fallbackRanking[0]).pipe(shareReplay(1)));
                Object.values(fbMap).forEach(t$ => t$.subscribe({ error: () => { } }));

                const allFallbackTasks$ = forkJoin(
                  Object.keys(fbMap).reduce((acc: any, key) => {
                    const m = parseInt(key);
                    acc[key] = fbMap[m].pipe(catchError(() => of(null)));
                    return acc;
                  }, {})
                );

                allFallbackTasks$.subscribe({
                  next: (results: any) => {
                    const primaryMcode = fallbackRanking.find(m => results[m]);
                    if (!primaryMcode) { observer.error('Absolute fallback failed'); return; }

                    const primaryResult = results[primaryMcode];
                    const member_responses: any = {};
                    Object.keys(results).forEach(key => {
                      const res = results[key];
                      if (res && res.event === 'final_result') {
                        const m = parseInt(key);
                        const moduleKey = m === 1 ? 'snapshot' : (m === 2 ? 'brand_analysis' : 'crosstab');
                        member_responses[moduleKey] = res.data?.result || res.data?.final_output || res.data || {};
                      }
                    });

                    if (primaryResult.data) {
                      primaryResult.data.member_responses = member_responses;
                    }

                    observer.next(primaryResult);
                    observer.complete();
                  },
                  error: (e) => observer.error(e)
                });
              }
            });
            return () => deciderSub.unsubscribe();
          });
        }

        return comp.apiService.snapshotAgentStream(comp.selected_client, comp.selected_study, comp.guidChatId, token, comp.inputValue, true, false, true, 0);
      })
    );
  }

  static prepareDiagnosticPayload(comp: any): any {
    try {
      if (comp.availableViews && comp.availableViews.length > 1 && comp.allMemberResponses) {
        const payloads: any[] = [];
        for (const view of comp.availableViews) {
          const viewData = comp.allMemberResponses[view.key] || {};
          const payload: any = {
            mcode: view.mcode,
            user_input: viewData.user_input || comp.promptData?.user_input,
            isTotalBase: viewData.isTotalBase ?? comp.isTotalBase,
            isBrandAnalysis: view.mcode === 2,
            entryPointData: comp.dataArray || [],
            Row: viewData.Row || viewData.row || (view.mcode === comp.mcode ? comp.dataObject?.Row : []) || [],
            Column: viewData.Column || viewData.column || (view.mcode === comp.mcode ? comp.dataObject?.Column : []) || [],
            Filter: viewData.Filter || viewData.filters || viewData.filter || (view.mcode === comp.mcode ? comp.dataObject?.Filter : []) || [],
            RowNesting: viewData.RowNesting || viewData.row_nesting || (view.mcode === comp.mcode ? comp.dataObject?.RowNesting : []) || [],
            ColNesting: viewData.ColNesting || viewData.column_nesting || (view.mcode === comp.mcode ? comp.dataObject?.ColNesting : []) || [],
            clientCode: comp.landingdata?.client_code || comp.selected_client,
            studyname: comp.landingdata?.study || comp.selected_study,
            significance: comp.DefaultConfidenceLevel || 'None',
            confidenceLevel: comp.DefaultConfidenceLevel || 'None',
            SelectedWeight: comp.selectedWeight || 'weight',
            Section: viewData.data?.snapshot_result?.snapshot?.widget_output?.section || comp.selectedSection || '',
            Snapshot_Name: viewData.data?.snapshot_result?.snapshot?.widget_output?.snapshot_name || comp.Snapshotdataforexport?.snapshotName || '',
            Snapshot_Type: viewData.data?.snapshot_result?.snapshot?.widget_output?.snapshot_type || comp._configService?.selectedSnapshotType || '',
            Widget_Id: view.mcode === 1 ? (viewData.Widget_ID || viewData.widget_id || comp.widget_Id || '') : '',
            Widget_Name: view.mcode === 1 ? (viewData.Widget_Name || viewData.widget_name || comp.widget_Name || '') : '',
            ...(viewData || {})
          };
          payloads.push(payload);
        }
        return payloads;
      }

      const payload: any = {
        mcode: comp.mcode,
        user_input: comp.promptData?.user_input,
        isTotalBase: comp.isTotalBase,
        isBrandAnalysis: comp.isBrandAnalysis,
        entryPointData: comp.dataArray || [],

        // Selection data
        Row: comp.dataObject?.Row || [],
        Column: comp.dataObject?.Column || [],
        Filter: comp.dataObject?.Filter || [],
        RowNesting: comp.dataObject?.RowNesting || [],
        ColNesting: comp.dataObject?.ColNesting || [],

        // Configuration
        clientCode: comp.landingdata?.client_code || comp.selected_client,
        studyname: comp.landingdata?.study || comp.selected_study,
        significance: comp.DefaultConfidenceLevel || 'None',
        confidenceLevel: comp.DefaultConfidenceLevel || 'None',
        SelectedWeight: comp.selectedWeight || 'weight',

        // Snapshot related
        Section: comp.selectedSection || comp.promptData?.data?.snapshot_result?.snapshot?.widget_output?.section || '',
        Snapshot_Name: comp.Snapshotdataforexport?.snapshotName || comp.promptData?.data?.snapshot_result?.snapshot?.widget_output?.snapshot_name || '',
        Snapshot_Type: comp._configService?.selectedSnapshotType || comp.promptData?.data?.snapshot_result?.snapshot?.widget_output?.snapshot_type || '',
        Widget_Id: comp.widget_Id || '',
        Widget_Name: comp.widget_Name || '',
        ...(comp.promptData || {})
      };
      return payload;
    } catch (error) {
      console.error('Error preparing diagnostic payload:', error);
      return comp.promptData || {};
    }
  }

  static constructSpecialized(comp: any, code: number, res: any, rawData: any = comp.orchestrationRawData) {
    const basePayload: any = {
      client_name: rawData?.client_name || comp.selected_client,
      study: rawData?.study || comp.selected_study,
      session_id: rawData?.session_id || comp.guidChatId,
      bearer_token: comp.currentOrchToken,
      significance: "None",
      confidence_level: "None",
      base: "ANSWER",
      weight: res?.weight || res?.Weight || comp.selectedWeight || "Unweighted"
    };

    let p = { ...basePayload, mcode: code };
    if (code === 1) {
      return {
        ...p,
        snapshot_type: res.snapshot_type || res.Snapshot_Type,
        primary_brands: res.Primary_Brands || res.primary_brands,
        primary_metrics: res.Primary_Metrics || res.primary_metrics || res.primary_metircs,
        market: res.Markets || res.market,
        timeperiod: res.Time_Period || res.timeperiod,
        Filter_Audience: res.Filter_Audience || res.filter_audience,
        Metric_Filters: res.Metric_Filters || res.metric_filters,
        Snapshot_Name: res.Snapshot_Name || res.snapshot_name,
        Section: res.Section || res.section,
        Widget_ID: res.Widget_ID || res.widget_id,
        Widget_Name: res.Widget_Name || res.widget_name,
        selected_doc_id: res.selected_doc_id,
        snapshot_index: rawData?.snapshot_index,
        widget_snapshot_index: rawData?.widget_snapshot_index
      };
    } else if (code === 2) {
      return {
        ...p,
        brand_analysis_index: rawData?.brand_analysis_index,
        row: res.Row,
        column: res.Column,
        row_nesting: res.Row_Nesting,
        column_nesting: res.Column_Nesting,
        filters: res.Filters
      };
    } else if (code === 3) {
      return {
        ...p,
        crosstab_index: rawData?.crosstab_index,
        row: res.Row,
        column: res.Column,
        row_nesting: res.Row_Nesting,
        column_nesting: res.Column_Nesting,
        filters: res.Filters
      };
    }
    return p;
  }

  static resolveEffectivePayloads(comp: any, target: any): { effectiveCrosstabPayload: any; effectiveBrandPayload: any } {
    const analytical = target?.analyticalData || target?.parsedData;
    const memberResponses = analytical?.member_responses;
    const brandView = memberResponses?.['brandanalysis'] || memberResponses?.['brand_analysis'];
    const crosstabView = memberResponses?.['crosstab'];

    const blockPayloadCrosstab =
      analytical?.getConfiguration?.crosstab_payload ||
      analytical?.AgentPayload?.getConfiguration?.crosstab_payload ||
      target?.crosstabPayload ||
      comp.extractViewPayload(crosstabView, ['crosstab_payload']);

    const blockPayloadBrand =
      analytical?.getConfiguration?.brand_analysis_payload ||
      analytical?.getConfiguration?.brand_anlysis_payload ||
      target?.brandAnalysisPayload ||
      comp.extractViewPayload(brandView, ['brand_analysis_payload', 'brand_anlysis_payload', 'crosstab_payload']);

    const resolved = {
      effectiveCrosstabPayload: blockPayloadCrosstab || comp.lastCrosstabPayload,
      effectiveBrandPayload: blockPayloadBrand || comp.lastBrandAnalysisPayload
    };
    return resolved;
  }

  static extractViewPayload(comp: any, view: any, keys: string[]): any {
    if (!view) return null;
    let cfg = view.getConfiguration;
    if (typeof cfg === 'string') {
      try { cfg = JSON.parse(cfg); } catch { cfg = null; }
    }
    if (Array.isArray(cfg)) cfg = cfg[0];
    const sources = [view, cfg, cfg?.AgentPayload?.getConfiguration, view?.AgentPayload?.getConfiguration];
    for (const src of sources) {
      if (!src) continue;
      for (const k of keys) {
        if (src[k]) return src[k];
      }
    }
    return null;
  }
}
